import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Input, SearchableSelectBox } from 'components';
import {
  updateUserInfoField,
  userInfoSelector,
} from 'redux/slices/signUp/signUpSlice';
import {
  useGetCountryListQuery,
  useGetCityListQuery,
  useGetDistrictsListQuery,
  useGetPostalCodeQuery,
} from 'redux/slices/signUp/SignUpApi';
import ActionButtons from '../ActionButtons/ActionButtons';

const AdressInfoForm = ({ onPrev, isLoading = false }) => {
  const dispatch = useDispatch();

  const [selectedCountry, setSelectedCountry] = useState('TR');
  const [countrySearch, setCountrySearch] = useState('Türkiye');
  const [citySearch, setCitySearch] = useState('');
  const [districtsSearch, setDistrictsSearch] = useState('');
  const [postalCodeSearch, setPostalCodeSearch] = useState('');
  const userInfo = useSelector(userInfoSelector);

  const [formErrors, setFormErrors] = useState({});
  const { formState, setValue } = useFormContext();
  const { errors } = formState;

  const { data = [], isFetching: i1 } = useGetCountryListQuery({
    country: countrySearch,
  });
  const { data: cities, isFetching: i2 } = useGetCityListQuery(
    { country: selectedCountry, city: citySearch },
    { skip: !selectedCountry || citySearch.length < 2 }
  );
  const { data: disctricts, isFetching: i3 } = useGetDistrictsListQuery(
    {
      country: selectedCountry,
      districts: districtsSearch,
      cityId: userInfo.cityCode,
    },
    { skip: !selectedCountry }
  );
  const { data: postalCodes, isFetching: i4 } = useGetPostalCodeQuery(
    {
      country: selectedCountry,
      cityId: userInfo.cityCode,
      district: districtsSearch,
      code: postalCodeSearch,
    },
    { skip: !selectedCountry }
  );

  useEffect(() => setFormErrors({ ...errors }), [errors]);

  useEffect(() => {
    setValue('countryName', userInfo?.countryName);
    setCountrySearch(userInfo?.countryName);
    setValue('countryCode', userInfo?.countryCode);
    setSelectedCountry(userInfo?.countryCode);
    setValue('cityCode', userInfo?.cityCode);
    setValue('cityName', userInfo?.cityName);
    setCitySearch(userInfo?.cityName);
    setValue('districtName', userInfo?.districtName);
    setDistrictsSearch(userInfo?.districtName);
    setValue('zipCode', userInfo?.zipCode);
    setPostalCodeSearch(userInfo?.zipCode);
    setValue('address', userInfo.address);
  }, [
    userInfo.countryName,
    userInfo.countryCode,
    userInfo.cityCode,
    userInfo.cityName,
    userInfo.districtName,
    userInfo.zipCode,
    userInfo.address,
  ]);

  const handleChangeUserInfo = (field, value) => {
    dispatch(updateUserInfoField({ field, value }));
  };

  const handleCountrySelect = (country) => {
    dispatch(
      updateUserInfoField({ field: 'countryCode', value: country.countryCode })
    );
    dispatch(
      updateUserInfoField({
        field: 'countryName',
        value: country.countryNameTR,
      })
    );
    setSelectedCountry(country.countryCode);
    setCountrySearch(country.countryNameTR);
  };

  const handleCitySelect = (city) => {
    dispatch(updateUserInfoField({ field: 'cityCode', value: city.cityId }));
    dispatch(updateUserInfoField({ field: 'cityName', value: city.cityName }));
    setCitySearch(city.cityName);
  };

  const handleDistrictSelect = (district) => {
    if (!district) return;
    
    dispatch(
      updateUserInfoField({
        field: 'zipCode',
        value: '',
      })
    );
    dispatch(
      updateUserInfoField({
        field: 'districtName',
        value: district.districtName,
      })
    );
    setDistrictsSearch(district.districtName);
  };

  const handlePostalCodeSelect = (zipcode) => {
    dispatch(
      updateUserInfoField({ field: 'zipCode', value: zipcode.postalcode })
    );
    setPostalCodeSearch(zipcode.postalcode);
  };

  return (
    <>
      <div className="flex  w-full overflow-y-auto sm:h-full sm:gap-x-4">
        <div className="flex flex-col w-full gap-y-4">
          <SearchableSelectBox
            maxLength={50}
            required
            inputValue={countrySearch}
            placeholder="Ülke"
            onChange={setCountrySearch}
            onSelect={handleCountrySelect}
            label="Ülke"
            inputName="countryCode"
            items={data?.data?.map((country) => {
              return {
                ...country,
                label: country.countryNameTR,
                value: country.countryNameEng,
              };
            })}
            inputErrors={formErrors?.countryName?.message}
            isLoading={i1}
          />
          <SearchableSelectBox
            maxLength={50}
            required
            inputValue={citySearch}
            placeholder="Şehir"
            isInputDisabled={!selectedCountry}
            onChange={setCitySearch}
            onSelect={handleCitySelect}
            label="Şehir"
            inputName="cityName"
            items={cities?.data?.map((city) => {
              return { ...city, label: city.cityName, value: city.cityId };
            })}
            inputErrors={formErrors?.cityName?.message}
            isLoading={i2}
          />
          <SearchableSelectBox
            required
            maxLength={50}
            inputValue={districtsSearch}
            placeholder="İlçe"
            isInputDisabled={!(userInfo.cityCode && userInfo.cityName)}
            onChange={setDistrictsSearch}
            onSelect={handleDistrictSelect}
            label="İlçe"
            inputName="districtName"
            items={disctricts?.data?.filter(city => city?.districtName).map((city) => {
              return {
                ...city,
                label: city.districtName,
                value: city.districtId,
              };
            })}
            inputErrors={formErrors?.districtName?.message}
            isLoading={i3}
          />
          <SearchableSelectBox
            maxLength={10}
            required
            inputValue={postalCodeSearch}
            isInputDisabled={!userInfo.districtName}
            placeholder="Posta Kodu"
            onChange={setPostalCodeSearch}
            onSelect={handlePostalCodeSelect}
            label="Posta Kodu"
            inputName="zipCode"
            items={postalCodes?.data?.filter(postalCode => postalCode?.districtName && postalCode?.postalcode).map((postalCode) => {
              return {
                ...postalCode,
                label: postalCode.districtName + ' / ' + postalCode.postalcode,
                value: postalCode.locid,
              };
            })}
            inputErrors={formErrors?.zipCode?.message}
            isLoading={i4}
          />
          <Input
            required
            maxLength={250}
            label="Açık adres"
            name="address"
            autoComplete
            id="address"
            value={userInfo.address}
            onChange={(e) =>
              handleChangeUserInfo(e.target.name, e.target.value)
            }
            errors={formErrors?.address?.message}
          />
          <div className="flex w-full justify-end mt-10">
            <ActionButtons onPrev={onPrev} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
};

AdressInfoForm.propTypes = {
  onPrev: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default AdressInfoForm;
